import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ArrowRight,
  ChevronDown,
  ArrowUpRight,
  Sparkles,
  Zap,
  BookOpen,
  Phone,
  CheckCircle2,
} from "lucide-react";
import { FOOTER_LOGO_URL } from "../../data/site";
import { MEGA_NAV } from "../../data/nav";

// Per-menu featured card content
const MENU_FEATURE = {
  services: {
    eyebrow: "Free Tool",
    title: "Audit your app in 15 seconds.",
    desc: "Get a live ASO score, top 3 quick wins and competitor benchmark — instant, no signup.",
    bullets: ["Live App Store + Play scrape", "0–100 score with breakdown", "Top 3 competitor compare"],
    cta: { label: "Run Free Audit", href: "/free-aso-audit" },
    accent: "electric",
    icon: Zap,
  },
  resources: {
    eyebrow: "Latest Playbook",
    title: "The 2026 ASO Operator's Field Guide.",
    desc: "Keyword research frameworks, screenshot A/B test results and ranking signal weights — straight from our 100+ app engagements.",
    bullets: ["Updated quarterly", "Free, no email gate", "Apple + Google Play"],
    cta: { label: "Read the Guide", href: "/blog" },
    accent: "amber",
    icon: BookOpen,
  },
  company: {
    eyebrow: "Talk to a Human",
    title: "Book a 30-min growth call.",
    desc: "We'll review your app live, flag the top 3 levers and hand you a 90-day roadmap — even if you don't sign up.",
    bullets: ["No sales pitch, real audit", "Founders welcome", "Same-week availability"],
    cta: { label: "Book a Call", href: "/contact" },
    accent: "emerald",
    icon: Phone,
  },
};

export default function Navbar() {
  const [open, setOpen] = useState(false); // mobile
  const [activeMenu, setActiveMenu] = useState(null); // desktop mega
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    setOpen(false);
    setActiveMenu(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  const handleEnter = (key) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(key);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveMenu(null), 120);
  };

  const headerCls = "bg-white border-b border-zinc-200";
  const linkCls = "text-charcoal hover:text-electric";
  const activeLinkCls = "text-electric";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 ${headerCls}`}
      data-testid="site-navbar"
      onMouseLeave={handleLeave}
    >
      <div className="container flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center"
          data-testid="nav-logo-link"
          aria-label="AppFillip — home"
        >
          <img
            src={FOOTER_LOGO_URL}
            alt="AppFillip — App Store Optimization Agency logo"
            width="160"
            height="40"
            className="h-9 md:h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop mega */}
        <nav
          className="hidden lg:flex items-center gap-1"
          aria-label="Main Navigation"
        >
          {MEGA_NAV.map((menu) => (
            <div
              key={menu.key}
              className="relative"
              onMouseEnter={() => handleEnter(menu.key)}
            >
              <button
                type="button"
                onClick={() =>
                  setActiveMenu(activeMenu === menu.key ? null : menu.key)
                }
                data-testid={`nav-menu-${menu.key}`}
                aria-expanded={activeMenu === menu.key}
                className={`px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 ${
                  activeMenu === menu.key ? activeLinkCls : linkCls
                }`}
              >
                {menu.label}
                <ChevronDown
                  className={`w-3.5 h-3.5 ${
                    activeMenu === menu.key ? "rotate-180 text-electric" : ""
                  }`}
                />
              </button>
            </div>
          ))}
          <Link
            to="/build-your-plan"
            data-testid="nav-link-build-plan"
            className={`px-4 py-2 text-sm font-medium ${linkCls}`}
          >
            Build Your Plan
          </Link>
          <Link
            to="/contact"
            data-testid="nav-link-contact"
            className={`px-4 py-2 text-sm font-medium ${linkCls}`}
          >
            Contact
          </Link>
        </nav>

        {/* CTA */}
        <div className="flex items-center gap-2">
          <Link
            to="/free-aso-audit"
            data-testid="nav-cta-audit"
            aria-label="Get a free ASO audit"
            className="hidden sm:inline-flex items-center gap-2 bg-electric text-white px-4 py-2.5 text-sm font-semibold hover:bg-electric-400 shadow-[3px_3px_0_0_#2D2D2D]"
          >
            Get Free ASO Audit
            <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden p-2 text-charcoal hover:text-electric"
            aria-label="Toggle navigation"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Desktop mega panel */}
      {activeMenu && (
        <div
          className="hidden lg:block absolute inset-x-0 top-full border-t border-zinc-200 bg-white shadow-[0_24px_48px_-24px_rgba(45,45,45,0.22)]"
          onMouseEnter={() => handleEnter(activeMenu)}
          data-testid={`mega-panel-${activeMenu}`}
        >
            {/* Top accent line */}
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-electric to-transparent" />

            <div className="container py-9 md:py-12 grid grid-cols-12 gap-10">
              {(() => {
                const menu = MEGA_NAV.find((m) => m.key === activeMenu);
                const feature = MENU_FEATURE[menu.key];
                const FeatureIcon = feature.icon;
                const accentMap = {
                  electric: { bg: "bg-electric", text: "text-electric", soft: "bg-electric/10" },
                  amber: { bg: "bg-amber-500", text: "text-amber-500", soft: "bg-amber-500/10" },
                  emerald: { bg: "bg-emerald-500", text: "text-emerald-500", soft: "bg-emerald-500/10" },
                };
                const ax = accentMap[feature.accent];

                return (
                  <>
                    {/* Featured CTA card */}
                    <div className="col-span-4">
                      <div className="relative h-full bg-charcoal text-white p-6 overflow-hidden group">
                        {/* Subtle grid + accent glow */}
                        <div className="absolute inset-0 bg-grid-pattern-dark opacity-30 pointer-events-none" />
                        <div
                          className={`absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-50 ${ax.soft}`}
                        />

                        <div className="relative">
                          <div className="flex items-center gap-2 mb-4">
                            <span className={`w-7 h-7 grid place-items-center ${ax.bg}`}>
                              <FeatureIcon className="w-3.5 h-3.5 text-white" strokeWidth={2.6} />
                            </span>
                            <span className={`text-[10px] font-black tracking-[0.2em] ${ax.text} uppercase`}>
                              {feature.eyebrow}
                            </span>
                          </div>

                          <h3 className="font-display text-xl md:text-[1.4rem] font-extrabold leading-tight text-white">
                            {feature.title}
                          </h3>
                          <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                            {feature.desc}
                          </p>

                          <ul className="mt-5 space-y-2">
                            {feature.bullets.map((b) => (
                              <li
                                key={b}
                                className="flex items-start gap-2 text-xs text-zinc-300"
                              >
                                <CheckCircle2
                                  className={`w-3.5 h-3.5 mt-0.5 ${ax.text} flex-shrink-0`}
                                  strokeWidth={2.4}
                                />
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>

                          <Link
                            to={feature.cta.href}
                            className={`mt-6 inline-flex items-center gap-2 ${ax.bg} text-white px-4 py-2.5 text-sm font-bold shadow-[3px_3px_0_0_#ffffff15]`}
                          >
                            {feature.cta.label}
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Right side — columns */}
                    <div
                      className={`col-span-8 grid gap-x-8 gap-y-2 ${
                        menu.columns.length >= 3 ? "grid-cols-3" : "grid-cols-2"
                      }`}
                    >
                      {menu.columns.map((col) => {
                        const isPremium = col.title === "Premium";
                        return (
                          <div key={col.title}>
                            {isPremium ? (
                              <div className="mb-4 flex items-center gap-2">
                                <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-electric text-white text-[10px] font-black tracking-[0.2em] px-2 py-1">
                                  <Sparkles className="w-2.5 h-2.5" />
                                  PREMIUM
                                </span>
                                <span className="h-px flex-1 bg-zinc-200" />
                              </div>
                            ) : (
                              <p className="text-[10px] font-black tracking-[0.2em] text-zinc-400 uppercase mb-4 flex items-center gap-2">
                                {col.title}
                                <span className="h-px flex-1 bg-zinc-200" />
                              </p>
                            )}

                            {isPremium ? (
                              <ul className="space-y-px bg-zinc-100">
                                {col.items.map((it) => {
                                  const Icon = it.icon;
                                  return (
                                    <li key={it.href}>
                                      <Link
                                        to={it.href}
                                        data-testid={`mega-link-${it.href.replace(/[/#]+/g, "-").replace(/^-/, "")}`}
                                        className="group flex items-center justify-between gap-2 px-3 py-2.5 bg-white hover:bg-zinc-50"
                                      >
                                        <span className="flex items-center gap-2.5 min-w-0">
                                          <Icon
                                            className="w-3.5 h-3.5 text-electric flex-shrink-0"
                                            strokeWidth={2.4}
                                          />
                                          <span className="text-[13px] font-semibold text-charcoal group-hover:text-electric truncate">
                                            {it.label}
                                          </span>
                                        </span>
                                        <ArrowUpRight className="w-3.5 h-3.5 text-zinc-300 group-hover:text-electric flex-shrink-0" />
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <ul className="space-y-0.5">
                                {col.items.map((it) => {
                                  const Icon = it.icon;
                                  return (
                                    <li key={it.href}>
                                      <Link
                                        to={it.href}
                                        data-testid={`mega-link-${it.href.replace(/[/#]+/g, "-").replace(/^-/, "")}`}
                                        className="group relative flex items-start gap-3 p-2.5 -mx-2.5 hover:bg-zinc-50"
                                      >
                                        {/* Left accent shown on hover */}
                                        <span className="absolute left-0 top-2.5 bottom-2.5 w-0.5 bg-electric hidden group-hover:block" />
                                        <span className="w-8 h-8 grid place-items-center bg-zinc-100 group-hover:bg-electric group-hover:text-white flex-shrink-0">
                                          <Icon className="w-4 h-4" strokeWidth={2.2} />
                                        </span>
                                        <span className="min-w-0">
                                          <span className="block text-[13px] font-semibold text-charcoal group-hover:text-electric leading-snug">
                                            {it.label}
                                          </span>
                                          <span className="block text-[11px] text-zinc-500 mt-0.5 leading-snug">
                                            {it.desc}
                                          </span>
                                        </span>
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Bottom utility strip */}
            <div className="border-t border-zinc-100 bg-zinc-50">
              <div className="container py-3 flex items-center justify-between text-xs">
                <div className="flex items-center gap-5 text-zinc-500">
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                    100+ apps · 20+ markets
                  </span>
                  <span className="hidden md:inline">★ 4.9 avg client rating</span>
                </div>
                <Link
                  to="/contact"
                  className="font-semibold text-charcoal hover:text-electric inline-flex items-center gap-1"
                >
                  Talk to an expert <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
        </div>
      )}

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden bg-white border-t border-zinc-200 max-h-[calc(100vh-4rem)] overflow-y-auto"
          data-testid="nav-mobile-panel"
        >
          <nav className="container py-5 flex flex-col" aria-label="Mobile">
            {MEGA_NAV.map((menu) => {
              const expanded = mobileExpanded === menu.key;
              return (
                <div key={menu.key} className="border-b border-zinc-100">
                  <button
                    onClick={() =>
                      setMobileExpanded(expanded ? null : menu.key)
                    }
                    className="w-full py-3 flex items-center justify-between text-base font-semibold text-charcoal"
                    data-testid={`nav-mobile-menu-${menu.key}`}
                    aria-expanded={expanded}
                  >
                    {menu.label}
                    <ChevronDown
                      className={`w-4 h-4 ${expanded ? "rotate-180" : ""}`}
                    />
                  </button>
                  {expanded && (
                    <ul className="pb-3 pl-2 space-y-2">
                      {menu.columns
                        .flatMap((c) =>
                          c.items.map((it) => ({ ...it, _premium: c.title === "Premium" }))
                        )
                        .map((it) => (
                          <li key={it.href}>
                            <Link
                              to={it.href}
                              onClick={() => setOpen(false)}
                              className="flex items-center gap-2 py-2 text-sm text-zinc-700 hover:text-electric"
                              data-testid={`nav-mobile-link-${it.href.replace(/[/#]+/g, "-").replace(/^-/, "")}`}
                            >
                              {it._premium && (
                                <span className="bg-gradient-to-r from-amber-400 to-electric text-white text-[9px] font-black tracking-widest px-1.5 py-0.5 flex-shrink-0">
                                  PREMIUM
                                </span>
                              )}
                              <span>{it.label}</span>
                            </Link>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              );
            })}
            <Link
              to="/build-your-plan"
              onClick={() => setOpen(false)}
              className="py-3 text-base font-semibold text-charcoal border-b border-zinc-100"
              data-testid="nav-mobile-build-plan"
            >
              Build Your Plan
            </Link>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="py-3 text-base font-semibold text-charcoal border-b border-zinc-100"
              data-testid="nav-mobile-contact"
            >
              Contact
            </Link>
            <Link
              to="/free-aso-audit"
              onClick={() => setOpen(false)}
              className="mt-5 inline-flex items-center justify-center gap-2 bg-electric text-white px-4 py-3 text-sm font-semibold"
              data-testid="nav-mobile-cta"
            >
              Get Free ASO Audit <ArrowRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
